import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './Machines.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';

const ViewMachine = forwardRef(({ machineId, onBack, editMode = false, createMode = false, onMachineCreated, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const [machine, setMachine] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasChanges, setHasChanges] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [machineSkills, setMachineSkills] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedProficiency, setSelectedProficiency] = useState('BASIC');

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    fetchDepartments();
    fetchAvailableSkills();
    if (machineId && !createMode) {
      fetchMachine();
      fetchMachineSkills();
    } else if (createMode) {
      const newMachine = {
        machine_code: `MACH-${Date.now().toString(36).toUpperCase()}`,
        machine_name: '',
        machine_type: 'PRINTING',
        department_id: null,
        manufacturer: '',
        model: '',
        serial_number: '',
        capacity_per_hour: 0,
        status: 'ACTIVE',
        location: '',
        installation_date: '',
        last_maintenance: '',
        next_maintenance: ''
      };
      setMachine(newMachine);
      setEditData(newMachine);
    }
  }, [machineId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchMachine = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/machines/${machineId}`));
      if (response.ok) {
        const data = await response.json();
        setMachine(data);

        // Filter to only valid database fields to prevent sending invalid fields later
        const filteredData = {
          machine_code: data.machine_code || '',
          machine_name: data.machine_name || '',
          machine_type: data.machine_type || '',
          department_id: data.department_id || null,
          manufacturer: data.manufacturer || '',
          model: data.model || '',
          serial_number: data.serial_number || '',
          capacity_per_hour: data.capacity_per_hour || 0,
          status: data.status || 'ACTIVE',
          location: data.location || '',
          installation_date: data.installation_date || '',
          last_maintenance: data.last_maintenance || '',
          next_maintenance: data.next_maintenance || ''
        };

        setEditData(filteredData);
      } else {
        setError(t('machines.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching machine:', error);
      setError(t('machines.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiFetch(API_CONFIG.ENDPOINTS.DEPARTMENTS, {
      });

      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      } else {
        console.error('Failed to fetch departments');
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const fetchMachineSkills = async () => {
    if (!machineId) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/skills/machines/${machineId}/skills`));
      if (response.ok) {
        const data = await response.json();
        setMachineSkills(data.machine_skills || []);
      }
    } catch (error) {
      console.error('Error fetching machine skills:', error);
    }
  };

  const fetchAvailableSkills = async () => {
    try {
      const response = await apiFetch(API_CONFIG.ENDPOINTS.SKILLS + '?limit=1000');
      if (response.ok) {
        const data = await response.json();
        setAvailableSkills(data.skills || []);
      }
    } catch (error) {
      console.error('Error fetching available skills:', error);
    }
  };

  const handleAddMachineSkill = async (skillId, proficiencyLevel) => {
    try {
      const response = await apiFetch(getApiUrl('/api/skills/machines/skills'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          machine_id: machineId,
          skill_id: skillId,
          proficiency_level: proficiencyLevel,
          certified_date: localToday()
        })
      });

      if (response.ok) {
        showSuccess(t('machines.view.messages.skillAddedSuccess'));
        fetchMachineSkills();
        setShowAddSkill(false);
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error adding skill:', error);
      showError(t('machines.view.messages.errorAddingSkill'));
    }
  };

  const handleRemoveMachineSkill = async (machineSkillId) => {
    if (!window.confirm(t('machines.view.messages.removeSkillConfirmation'))) return;

    try {
      const response = await apiFetch(getApiUrl(`/api/skills/machines/skills/${machineSkillId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('machines.view.messages.skillRemovedSuccess'));
        fetchMachineSkills();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error removing skill:', error);
      showError(t('machines.view.messages.errorRemovingSkill'));
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const url = createMode
        ? API_CONFIG.ENDPOINTS.MACHINES
        : getApiUrl(`/api/machines/${machineId}`);

      const method = createMode ? 'POST' : 'PUT';

      // Filter to only include valid database fields
      const validMachineData = {
        machine_code: editData.machine_code,
        machine_name: editData.machine_name,
        machine_type: editData.machine_type,
        department_id: editData.department_id || null,
        manufacturer: editData.manufacturer,
        model: editData.model,
        serial_number: editData.serial_number,
        capacity_per_hour: editData.capacity_per_hour || null,
        status: editData.status,
        location: editData.location,
        installation_date: editData.installation_date || null,
        last_maintenance: editData.last_maintenance || null,
        next_maintenance: editData.next_maintenance || null
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(validMachineData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        showSuccess(
          createMode ? t('machines.view.messages.machineCreated') : t('machines.view.messages.machineUpdated'),
          createMode ? t('machines.view.messages.machineCreatedSuccess') : t('machines.view.messages.machineUpdatedSuccess')
        );

        if (createMode && result.machine_id) {
          setMachine(result);
          setEditData(result);
          if (onMachineCreated) {
            onMachineCreated(result.machine_id);
          }
        } else if (!createMode) {
          await fetchMachine();
        }

        if (onSave) onSave(result);
      } else {
        const parsed = await showApiError(response, showError, tCommon);
        setError(parsed.message);
        return;
      }
    } catch (error) {
      console.error('Error saving machine:', error);
      setError(t('machines.view.messages.errorSavingMachine'));
      showError(
        createMode ? t('machines.view.messages.failedToCreateMachine') : t('machines.view.messages.failedToUpdateMachine'),
        t('machines.view.messages.errorSavingMachine')
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(machine);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('machines.view.messages.deleteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/machines/${machineId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('machines.view.messages.machineDeleted'), t('machines.view.messages.machineDeletedSuccess'));
        if (onBack) onBack();
      } else {
        const parsed = await showApiError(response, showError, tCommon);
        setError(parsed.message);
      }
    } catch (error) {
      console.error('Error deleting machine:', error);
      setError(t('machines.view.messages.errorDeletingMachine'));
      showError(t('machines.view.messages.failedToDeleteMachine'), t('machines.view.messages.errorDeletingMachine'));
    }
  };

  const renderOverviewTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('machines.view.sections.basicInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('machines.view.fields.machineCode')} *</label>
            <input
              type="text"
              value={editData.machine_code || ''}
              onChange={(e) => handleFieldChange('machine_code', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.machineName')} *</label>
            <input
              type="text"
              value={editData.machine_name || ''}
              onChange={(e) => handleFieldChange('machine_name', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.machineType')} *</label>
            <select
              value={editData.machine_type || 'PRINTING'}
              onChange={(e) => handleFieldChange('machine_type', e.target.value)}
              disabled={!editMode && !createMode}
              required
            >
              <option value="PRINTING">{t('machines.view.types.printingPress')}</option>
              <option value="CUTTING">{t('machines.view.types.cuttingMachine')}</option>
              <option value="FOLDING">{t('machines.view.types.foldingMachine')}</option>
              <option value="LAMINATING">{t('machines.view.types.laminatingMachine')}</option>
              <option value="PACKAGING">{t('machines.view.types.packagingMachine')}</option>
              <option value="OTHER">{t('machines.view.types.other')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.status')} *</label>
            <select
              value={editData.status || 'ACTIVE'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode && !createMode}
              required
            >
              <option value="ACTIVE">{t('machines.view.status.active')}</option>
              <option value="MAINTENANCE">{t('machines.view.status.maintenance')}</option>
              <option value="INACTIVE">{t('machines.view.status.inactive')}</option>
              <option value="BROKEN">{t('machines.view.status.broken')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.department')}</label>
            <select
              value={editData.department_id || ''}
              onChange={(e) => handleFieldChange('department_id', e.target.value ? parseInt(e.target.value) : null)}
              disabled={!editMode && !createMode}
            >
              <option value="" disabled hidden>{t('machines.view.placeholders.selectDepartment')}</option>
              {departments.map(dept => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name} ({dept.department_code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('machines.view.sections.equipmentDetails')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('machines.view.fields.manufacturer')}</label>
            <input
              type="text"
              value={editData.manufacturer || ''}
              onChange={(e) => handleFieldChange('manufacturer', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.model')}</label>
            <input
              type="text"
              value={editData.model || ''}
              onChange={(e) => handleFieldChange('model', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.serialNumber')}</label>
            <input
              type="text"
              value={editData.serial_number || ''}
              onChange={(e) => handleFieldChange('serial_number', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.installationDate')}</label>
            <input
              type="date"
              value={editData.installation_date || ''}
              onChange={(e) => handleFieldChange('installation_date', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('machines.view.sections.locationAssignment')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('machines.view.fields.location')}</label>
            <input
              type="text"
              value={editData.location || ''}
              onChange={(e) => handleFieldChange('location', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.capacityPerHour')}</label>
            <input
              type="number"
              step="0.01"
              value={editData.capacity_per_hour || ''}
              onChange={(e) => handleFieldChange('capacity_per_hour', Number(e.target.value))}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSpecificationsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('machines.view.sections.machineStatus')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('machines.view.fields.status')}</label>
            <select
              value={editData.status || 'ACTIVE'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode && !createMode}
              className="form-select"
            >
              <option value="ACTIVE">{t('machines.view.status.active')}</option>
              <option value="MAINTENANCE">{t('machines.view.status.maintenance')}</option>
              <option value="INACTIVE">{t('machines.view.status.inactive')}</option>
              <option value="BROKEN">{t('machines.view.status.broken')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.machineType')}</label>
            <input
              type="text"
              value={editData.machine_type || ''}
              onChange={(e) => handleFieldChange('machine_type', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('machines.view.sections.maintenanceSchedule')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('machines.view.fields.lastMaintenance')}</label>
            <input
              type="date"
              value={editData.last_maintenance || ''}
              onChange={(e) => handleFieldChange('last_maintenance', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.nextMaintenance')}</label>
            <input
              type="date"
              value={editData.next_maintenance || ''}
              onChange={(e) => handleFieldChange('next_maintenance', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderOperationsTab = () => (
    <div className="tab-content">

      <div className="form-section">
        <h3>{t('machines.view.sections.maintenance')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('machines.view.fields.lastMaintenance')}</label>
            <input
              type="date"
              value={editData.last_maintenance || ''}
              onChange={(e) => handleFieldChange('last_maintenance', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('machines.view.fields.nextMaintenance')}</label>
            <input
              type="date"
              value={editData.next_maintenance || ''}
              onChange={(e) => handleFieldChange('next_maintenance', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('machines.view.sections.machineSummary')}</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>{t('machines.view.fields.machineId')}</label>
            <span>{machine?.machine_id || t('machines.view.titles.newMachine')}</span>
          </div>
          <div className="info-item">
            <label>{t('machines.view.fields.createdDate')}</label>
            <span>{machine?.created_date ? formatAppDate(machine.created_date) : 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>{t('machines.view.fields.updatedDate')}</label>
            <span>{machine?.updated_date ? formatAppDate(machine.updated_date) : 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const handleAddSkill = () => {
    if (!selectedSkill) {
      showWarning(t('machines.view.messages.selectSkillWarning'));
      return;
    }
    handleAddMachineSkill(parseInt(selectedSkill), selectedProficiency);
    setSelectedSkill('');
    setSelectedProficiency('BASIC');
  };

  const renderSkillsTab = () => {
    const proficiencyColors = {
      BASIC: '#93c5fd',
      INTERMEDIATE: '#a5f3fc',
      ADVANCED: '#86efac',
      EXPERT: '#fde047'
    };

    return (
      <div className="tab-content">
        <div className="form-section">
          <h3>{t('machines.view.sections.machineSkills')}</h3>

          {(editMode || createMode) && !createMode && (
            <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px' }}>{t('machines.view.skills.addNewSkill')}</h4>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>{t('machines.view.skills.skill')}</label>
                  <select
                    value={selectedSkill}
                    onChange={(e) => setSelectedSkill(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                  >
                    <option value="" disabled hidden>{t('machines.view.skills.selectSkill')}</option>
                    {availableSkills
                      .filter(skill => !machineSkills.find(ms => ms.skill_id === skill.skill_id))
                      .map(skill => (
                        <option key={skill.skill_id} value={skill.skill_id}>
                          {skill.skill_name} ({skill.skill_category})
                        </option>
                      ))
                    }
                  </select>
                </div>
                <div style={{ flex: '0 0 180px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>{t('machines.view.skills.proficiency')}</label>
                  <select
                    value={selectedProficiency}
                    onChange={(e) => setSelectedProficiency(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                  >
                    <option value="BASIC">{t('machines.view.skills.proficiencyLevels.basic')}</option>
                    <option value="INTERMEDIATE">{t('machines.view.skills.proficiencyLevels.intermediate')}</option>
                    <option value="ADVANCED">{t('machines.view.skills.proficiencyLevels.advanced')}</option>
                    <option value="EXPERT">{t('machines.view.skills.proficiencyLevels.expert')}</option>
                  </select>
                </div>
                <button
                  onClick={handleAddSkill}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--accent-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {t('machines.view.skills.addSkill')}
                </button>
              </div>
            </div>
          )}

          {machineSkills.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
              <div>{t('machines.view.skills.noSkillsAssigned')}</div>
              {(editMode || createMode) && <div style={{ fontSize: '14px', marginTop: '8px' }}>{t('machines.view.skills.addSkillsHint')}</div>}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
              {machineSkills.map(ms => (
                <div
                  key={ms.machine_skill_id}
                  style={{
                    padding: '16px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    background: 'var(--bg-secondary)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>{ms.skill_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                        {ms.skill_code}
                      </div>
                    </div>
                    {(editMode || createMode) && (
                      <button
                        onClick={() => handleRemoveMachineSkill(ms.machine_skill_id)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {t('machines.view.skills.remove')}
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        background: proficiencyColors[ms.proficiency_level],
                        color: '#000'
                      }}
                    >
                      {ms.proficiency_level}
                    </span>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-secondary)'
                      }}
                    >
                      {ms.skill_category}
                    </span>
                  </div>
                  {ms.certified_date && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {t('machines.view.skills.certified')}: {formatAppDate(ms.certified_date)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('machines.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!machine && !createMode) {
    return <div className="view-container"><div className="loading">{t('machines.view.titles.noMachineFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('machines.view.titles.newMachine') : editData.machine_name || t('machines.view.titles.machine')}
          </h1>
          {!createMode && editData.status && (
            <span className={`status-badge ${
              editData.status === 'ACTIVE' ? 'status-active' :
              editData.status === 'MAINTENANCE' ? 'status-warning' :
              editData.status === 'INACTIVE' ? 'status-inactive' :
              editData.status === 'BROKEN' ? 'status-error' : 'status-inactive'
            }`}>
              {editData.status}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('machines.toolbar.buttons.edit')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('machines.toolbar.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('machines.toolbar.buttons.create') : t('machines.toolbar.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('machines.view.tabs.overview')}
          </button>
          <button
            className={`tab-item ${activeTab === 'specifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('specifications')}
          >
            {t('machines.view.tabs.specifications')}
          </button>
          <button
            className={`tab-item ${activeTab === 'operations' ? 'active' : ''}`}
            onClick={() => setActiveTab('operations')}
          >
            {t('machines.view.tabs.operations')}
          </button>
          <button
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            {t('machines.view.tabs.notes')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'skills' ? 'active' : ''}`}
              onClick={() => setActiveTab('skills')}
            >
              {t('machines.view.tabs.skills')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'specifications' && renderSpecificationsTab()}
          {activeTab === 'operations' && renderOperationsTab()}
          {activeTab === 'notes' && renderNotesTab()}
          {activeTab === 'skills' && renderSkillsTab()}
        </div>
      </div>
    </div>
  );
});

ViewMachine.displayName = 'ViewMachine';

export default ViewMachine;